import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { FONT_CATEGORIES, FONT_LIBRARY, FontCategory, preloadLibraryStylesheets } from './fonts';

/**
 * Font chooser: search, category filter, every family previewed in its own
 * face. The brand typeface (when the kit names one) is pinned on top.
 */
export const FontPicker: React.FC<{
    value: string;
    onChange: (family: string) => void;
    brandFont?: string;
    className?: string;
}> = ({ value, onChange, brandFont, className }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [cat, setCat] = useState<FontCategory | 'all'>('all');

    useEffect(() => { if (open) preloadLibraryStylesheets(); }, [open]);

    const list = useMemo(() => {
        const q = query.trim().toLowerCase();
        return FONT_LIBRARY.filter((f) => (cat === 'all' || f.category === cat) && (!q || f.family.toLowerCase().includes(q)));
    }, [query, cat]);

    const brandInLibrary = brandFont && FONT_LIBRARY.some((f) => f.family.toLowerCase() === brandFont.toLowerCase());

    const Row: React.FC<{ family: string; note?: string }> = ({ family, note }) => (
        <button
            type="button"
            onClick={() => { onChange(family); setOpen(false); }}
            className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-start transition-colors hover:bg-accent',
                value === family && 'bg-foreground/[0.06]',
            )}
        >
            <span className="min-w-0 flex-1 truncate text-[15px] leading-tight text-foreground" style={{ fontFamily: `"${family}", sans-serif` }}>
                {family}
            </span>
            {note && <span className="shrink-0 text-[11px] text-muted-foreground">{note}</span>}
            {value === family && <Check className="size-3.5 shrink-0 text-foreground" />}
        </button>
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title={t('DESIGN_EDITOR.FONT')}
                    className={cn(
                        'flex h-8 min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-foreground/[0.045] px-2.5 text-xs text-foreground transition-colors hover:bg-foreground/[0.07]',
                        className,
                    )}
                >
                    <span className="min-w-0 flex-1 truncate text-start" style={{ fontFamily: `"${value}", sans-serif` }}>{value}</span>
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={6} className="w-[288px] gap-0 p-0">
                <div className="flex items-center gap-2 border-b px-3 py-2">
                    <Search className="size-3.5 text-muted-foreground" />
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('DESIGN_EDITOR.FONT_SEARCH')}
                        className="h-7 w-full border-none bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
                    />
                </div>
                <div className="flex flex-wrap gap-1 border-b px-3 py-2">
                    {(['all', ...FONT_CATEGORIES] as const).map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setCat(c)}
                            className={cn(
                                'h-6 cursor-pointer rounded-full border px-2.5 text-[11px] transition-colors',
                                cat === c ? 'border-foreground font-medium text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {t(`DESIGN_EDITOR.FONT_CAT_${c.toUpperCase()}`)}
                        </button>
                    ))}
                </div>
                <div className="max-h-[340px] overflow-y-auto p-1.5">
                    {brandFont && cat === 'all' && !query && (
                        <>
                            <Row family={brandFont} note={t('DESIGN_EDITOR.FONT_BRAND')} />
                            {!brandInLibrary && <div className="px-2.5 pb-1 text-[11px] text-muted-foreground">{t('DESIGN_EDITOR.FONT_BRAND_HINT')}</div>}
                            <div className="my-1 h-px bg-border" />
                        </>
                    )}
                    {list.map((f) => <Row key={f.family} family={f.family} />)}
                    {list.length === 0 && (
                        <div className="px-2.5 py-6 text-center text-xs text-muted-foreground">{t('DESIGN_EDITOR.NOTHING_FOUND')}</div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};
